package com.shadowings.gero.data.lessons.chapter4


import com.shadowings.gero.data.lessons.inlineMeaning
import com.shadowings.gero.data.lessons.DistractorPool
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.shadowings.gero.data.lessons.calculateIndirectGenitivePhrases
import com.shadowings.gero.data.lessons.tokenizePhrase
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.TextTranslationContent
import com.shadowings.gero.domain.model.WordSelectionContent
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.exp_content_che_confusione
import gero.composeapp.generated.resources.exp_content_come_facciamo_a_distinguere_un_caso_dall_altro
import gero.composeapp.generated.resources.exp_content_fin_ora_abbiamo_visto_tre_significati_diversi_per_dei
import gero.composeapp.generated.resources.exp_content_fortunatamente_esiste_anche_il_genitivo_indiretto
import gero.composeapp.generated.resources.exp_content_genitivo_indiretto_esempio_1
import gero.composeapp.generated.resources.exp_content_genitivo_indiretto_esempio_2
import gero.composeapp.generated.resources.exp_content_iniziamo_col_caso_piu_semplice
import gero.composeapp.generated.resources.exp_content_lezione_finita_genitivo_indiretto_i
import gero.composeapp.generated.resources.exp_content_ovviamente_bisogna_ottimizzare_lo_spazio_quindi_il
import gero.composeapp.generated.resources.exp_content_quando_la_cosa_posseduta_e_singolare_e_maschile_basta
import gero.composeapp.generated.resources.exp_title_esempio_1
import gero.composeapp.generated.resources.exp_title_esempio_2
import gero.composeapp.generated.resources.exp_title_genitivo_indiretto_i
import gero.composeapp.generated.resources.exp_title_lezione_completata

fun getGenitivoIndirettoContent(): List<IExplanation> {
    return listOf(
        ExplanationContent(
            title = Res.string.exp_title_genitivo_indiretto_i,
            content = Res.string.exp_content_fin_ora_abbiamo_visto_tre_significati_diversi_per_dei,
            face = Faces.Idle
        ),
        ExplanationContent(
            title = Res.string.exp_title_genitivo_indiretto_i,
            content = Res.string.exp_content_che_confusione,
            face = Faces.Surprised
        ),
        ExplanationContent(
            title = Res.string.exp_title_genitivo_indiretto_i,
            content = Res.string.exp_content_come_facciamo_a_distinguere_un_caso_dall_altro,
            face = Faces.Sad
        ),
        ExplanationContent(
            title = Res.string.exp_title_genitivo_indiretto_i,
            content = Res.string.exp_content_fortunatamente_esiste_anche_il_genitivo_indiretto,
            face = Faces.Happy
        ),
        ExplanationContent(
            title = Res.string.exp_title_genitivo_indiretto_i,
            content = Res.string.exp_content_iniziamo_col_caso_piu_semplice,
            face = Faces.Serious
        ),
        ExplanationContent(
            title = Res.string.exp_title_esempio_1,
            content = Res.string.exp_content_quando_la_cosa_posseduta_e_singolare_e_maschile_basta,
            face = Faces.Happy,
        ),
        ExplanationContent(
            title = Res.string.exp_title_esempio_1,
            content = Res.string.exp_content_genitivo_indiretto_esempio_1,
            face = Faces.Happy,
            composable = {
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.Center
                ) {
                    Column {
                        MdCWord(
                            word = "D58-M17-V31-G5-M17-X1:I9-A1",
                            rtl = false,
                            cart = false
                        )
                        MdCWord(
                            word = "D58-M17-V31-G5-N35-M17-X1:I9-A1",
                            rtl = false,
                            cart = false
                        )
                    }
                }
            }
        ),
        ExplanationContent(
            title = Res.string.exp_title_esempio_2,
            content = Res.string.exp_content_ovviamente_bisogna_ottimizzare_lo_spazio_quindi_il,
            face = Faces.Happy,
        ),
        ExplanationContent(
            title = Res.string.exp_title_esempio_2,
            content = Res.string.exp_content_genitivo_indiretto_esempio_2,
            face = Faces.Happy
        ),
        ExplanationContent(
            title = Res.string.exp_title_lezione_completata,
            content = Res.string.exp_content_lezione_finita_genitivo_indiretto_i,
            face = Faces.Perfect
        )
    )
}

fun getGenitivoIndirettoQuizContent(words: List<Word>, languageCode: String): List<IQuizContent> {
    // Each triple: (possessed, connector, possessor)
    val triples = listOf(
        Triple("FALCO", ":N35-", "PADRE"),
        Triple("VINO", ":N35-", "PASTORE"),
        Triple("ACQUA", ":N35-", "MADRE"),
        Triple("GRANO", ":N35-", "PADRE"),
        Triple("PESCE", ":N35-", "GATTO"),
        Triple("INCENSO", "-N35-", "INEPU"),
        Triple("GATTO", ":N35-", "DONNA"),
        Triple("ALBERO", "-N35-", "PASTORE"),
        Triple("VINO", ":N35-", "SACERDOTE"),
        Triple("UCCELLO", ":N35-", "GATTO")
    ).shuffled()

    val firstPart = triples.take(7)
    val secondPart = triples.drop(7)

    return calculateIndirectGenitivePhrases(words, firstPart, languageCode)
        .map { phrase ->
            val possessedWord = phrase.possessedWord
            val possessorWord = phrase.possessorWord
            val correctTokens = phrase.correctTokens

            val otherWords = DistractorPool.words(words).filter {
                it.id != possessedWord.id && it.id != possessorWord.id &&
                        !it.getArticolo(languageCode).isNullOrBlank() &&
                        it.getArticolo(languageCode) != "-" &&
                        !it.getGenitivo(languageCode).isNullOrBlank() &&
                        it.getGenitivo(languageCode) != "-"
            }
            val wrongWords = otherWords.shuffled().take(3)
            // tokenizePhrase splits any multi-word field (a compound vocabulary meaning like
            // "König von Oberägypten", or a multi-word Spanish genitive like "de la") into
            // separate one-word tiles instead of one glued distractor tile.
            val wrongTokens = mutableListOf<String>()
            wrongWords.forEach { ww ->
                ww.getArticolo(languageCode)?.let { if (it != "-") wrongTokens.addAll(tokenizePhrase(it)) }
                val meaning = ww.inlineMeaning(languageCode, ww.getMeaning(languageCode).split(",")[0].trim())
                wrongTokens.addAll(tokenizePhrase(meaning))
                ww.getGenitivo(languageCode)?.let { if (it != "-") wrongTokens.addAll(tokenizePhrase(it)) }
            }

            // Never let a distractor collide (case-insensitively) with a token the correct answer
            // actually needs — otherwise a duplicate tile would silently swallow the real one.
            // distinct() only applies to the distractor pool: correctTokens can legitimately repeat
            // a word (e.g. "the wine of the priest" needs "the" twice), and deduping the combined
            // list would collapse that into a single tile, making the phrase unanswerable.
            val allWordsList = (correctTokens + wrongTokens.filter { wt ->
                correctTokens.none { it.trim().lowercase() == wt.trim().lowercase() }
            }.distinct())
                .filter { it.isNotBlank() }
                .shuffled()

            WordSelectionContent(
                hieroglyphs = phrase.hieroglyphs,
                correctPhrase = phrase.correctPhrase,
                words = allWordsList,
                transliteration = "${possessedWord.reading} ${phrase.particleTransliteration} ${possessorWord.reading}"
            )
        } + calculateIndirectGenitivePhrases(words, secondPart, languageCode)
        .map { phrase ->
            TextTranslationContent(
                hieroglyphs = phrase.hieroglyphs,
                correctPhrase = phrase.correctPhrase,
                transliteration = "${phrase.possessedWord.reading} ${phrase.particleTransliteration} ${phrase.possessorWord.reading}"
            )
        }
}



